import { Popover } from '@mantine/core';
import c from 'classnames';
import React, { useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { Field } from 'react-final-form';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import tw from 'twin.macro';
import { v4 as uuid } from 'uuid';
import { Feature } from '../../types/pricingPage';
import Button from '../Button';
import { newFeature } from '../../utils/plans';

const List = tw.div`px-0`;
const ListItem = tw.div`list-none rounded-sm p-3 flex`;

const makeOnDragEndFunction =
    (fields: FieldArrayRenderProps<Feature, HTMLInputElement>['fields']) => (result: DropResult) => {
        if (!result.destination) {
            return;
        }
        fields.move(result.source.index, result.destination.index);
    };

interface Props {
    push: (...args: any[]) => void;
    onCopy: (feature: string) => void;
}

export default function FeatureEditor({ push, onCopy }: Props) {
    return (
        <div className="grid grid-cols-1 gap-2">
            <div className="font-bold pl-4">Features</div>
            <List>
                <FieldArray<Feature> name="features">
                    {({ fields }) => (
                        <div>
                            <DragDropContext onDragEnd={makeOnDragEndFunction(fields)}>
                                <Droppable droppableId="droppable">
                                    {(provided) => (
                                        <div {...provided.droppableProps} ref={provided.innerRef}>
                                            {fields.map((featureFieldName, featureFieldIndex) => (
                                                <Draggable
                                                    key={featureFieldName}
                                                    draggableId={featureFieldName}
                                                    index={featureFieldIndex}
                                                >
                                                    {(provided, snapshot) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            style={provided.draggableProps.style}
                                                        >
                                                            <ListItem
                                                                key={featureFieldName}
                                                                className={c('rounded-lg', {
                                                                    'bg-gray-300': snapshot.isDragging,
                                                                })}
                                                            >
                                                                <div className="inline-flex items-center mr-2 text-gray-500 hover:text-black">
                                                                    <svg
                                                                        viewBox="0 0 1792 1792"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        className="h-4 w-4 fill-current"
                                                                    >
                                                                        <path d="M1664 1344v128q0 26-19 45t-45 19H192q-26 0-45-19t-19-45v-128q0-26 19-45t45-19h1408q26 0 45 19t19 45zm0-512v128q0 26-19 45t-45 19H192q-26 0-45-19t-19-45V832q0-26 19-45t45-19h1408q26 0 45 19t19 45zm0-512v128q0 26-19 45t-45 19H192q-26 0-45-19t-19-45V320q0-26 19-45t45-19h1408q26 0 45 19t19 45z" />
                                                                    </svg>
                                                                </div>
                                                                <Field name={`${featureFieldName}.name`}>
                                                                    {(props) => (
                                                                        <input
                                                                            {...props.input}
                                                                            onKeyUp={(
                                                                                event: React.KeyboardEvent<HTMLInputElement>
                                                                            ) => {
                                                                                if (event.key === 'Enter') {
                                                                                    push('features', {
                                                                                        id: uuid(),
                                                                                        name: '',
                                                                                        allPlans: false,
                                                                                        order: fields.length,
                                                                                    } as Feature); //TODO: focus on next input
                                                                                }
                                                                            }}
                                                                            className="flex-grow py-2 px-3 border-b text-grey-darkest flex-grow focus:outline-none focus:border-b-2 focus:border-blue-500"
                                                                            placeholder="Type plan feature ..."
                                                                            autoComplete="off"
                                                                        />
                                                                    )}
                                                                </Field>
                                                                <Field name={`${featureFieldName}.slug`}>
                                                                    {(props) => (
                                                                        <input
                                                                            {...props.input}
                                                                            className="focus:outline-none px-3 text-sm hidden"
                                                                            disabled
                                                                            autoComplete="off"
                                                                        />
                                                                    )}
                                                                </Field>
                                                                <FeatureConfigureButton
                                                                    featureFieldName={featureFieldName}
                                                                    onDelete={() => fields.remove(featureFieldIndex)}
                                                                    onCopy={() =>
                                                                        onCopy(fields.value[featureFieldIndex].name)
                                                                    }
                                                                />
                                                            </ListItem>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>
                            <div className="flex flex-row-reverse py-2 pr-2">
                                <Button
                                    type="button"
                                    kind="tertiary"
                                    onClick={() => {
                                        push('features', newFeature((fields.length || 0) + 1));
                                    }}
                                >
                                    Add Plan Feature
                                </Button>
                            </div>
                        </div>
                    )}
                </FieldArray>
            </List>
        </div>
    );
}

function FeatureConfigureButton({
    featureFieldName,
    onDelete,
    onCopy,
}: {
    featureFieldName: string;
    onDelete: () => void;
    onCopy: () => void;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const handleSetOpen = () => setIsOpen(true);
    return (
        <Popover opened={isOpen} onChange={setIsOpen} withArrow>
            <Popover.Target>
                <button type="button" onClick={handleSetOpen} className="h-full pl-2 place-self-center">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1792 1792" className="w-3 h-3">
                        <path d="M1152 896q0-106-75-181t-181-75-181 75-75 181 75 181 181 75 181-75 75-181zm512-109v222q0 12-8 23t-20 13l-185 28q-19 54-39 91 35 50 107 138 10 12 10 25t-9 23q-27 37-99 108t-94 71q-12 0-26-9l-138-108q-44 23-91 38-16 136-29 186-7 28-36 28H785q-14 0-24.5-8.5T749 1634l-28-184q-49-16-90-37l-141 107q-10 9-25 9-14 0-25-11-126-114-165-168-7-10-7-23 0-12 8-23 15-21 51-66.5t54-70.5q-27-50-41-99l-183-27q-13-2-21-12.5t-8-23.5V783q0-12 8-23t19-13l186-28q14-46 39-92-40-57-107-138-10-12-10-24 0-10 9-23 26-36 98.5-107.5T465 263q13 0 26 10l138 107q44-23 91-38 16-136 29-186 7-28 36-28h222q14 0 24.5 8.5T1043 158l28 184q49 16 90 37l142-107q9-9 24-9 13 0 25 10 129 119 165 170 7 8 7 22 0 12-8 23-15 21-51 66.5t-54 70.5q26 50 41 98l183 28q13 2 21 12.5t8 23.5z" />
                    </svg>
                </button>
            </Popover.Target>

            <Popover.Dropdown>
                <div className="flex flex-col border-gray-200 pb-2 rounded overflow-hidden">
                    <div className="bg-white border-b font-bold text-center">
                        <Field name={`${featureFieldName}.name`}>{(props) => props.input.value}</Field>
                    </div>
                    <div className="px-4 py-2 bg-white border-b">
                        <Field name={`${featureFieldName}.visible`} type="checkbox">
                            {(props) => (
                                <label className="inline-flex items-center ml-2 text-sm">
                                    <input {...props.input} type="checkbox" className="checkbox" defaultValue="false" />
                                    <span className="text-gray-700 ml-2">Visible</span>
                                </label>
                            )}
                        </Field>
                    </div>
                    <div className="px-4 py-2 bg-white border-b">
                        <Field name={`${featureFieldName}.subdued`} type="checkbox">
                            {(props) => (
                                <label className="inline-flex items-center ml-2 text-sm">
                                    <input {...props.input} type="checkbox" className="checkbox" defaultValue="false" />
                                    <span className="text-gray-700 ml-2">Subdued</span>
                                </label>
                            )}
                        </Field>
                    </div>
                    <div className="px-4 py-2 bg-white border-b">
                        <Field name={`${featureFieldName}.allPlans`} type="checkbox">
                            {(props) => (
                                <label className="inline-flex items-center ml-2 text-sm">
                                    <input {...props.input} type="checkbox" className="checkbox" defaultValue="false" />
                                    <span className="text-gray-700 ml-2">Apply to all Plans</span>
                                </label>
                            )}
                        </Field>
                    </div>
                    <div className="px-4 py-2 bg-white flex-grow flex items-end space-x-4">
                        <Button kind="outline" onClick={() => onCopy()}>
                            Copy to yearly
                        </Button>
                        <Button kind="danger" onClick={() => (window.confirm('Are you sure?') ? onDelete() : null)}>
                            Delete
                        </Button>
                    </div>
                </div>
            </Popover.Dropdown>
        </Popover>
    );
}
